import moment from 'moment';

export const buildQueryString = (dataToSend) => {
  // Create an array of key-value pairs
  const queryStringArray = Object.entries(dataToSend)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);

  // Join the array with "&" to form the final query string
  const queryString = queryStringArray.join('&');

  return queryString;
};

export const convertDateFormat = (timestamp) => {
  const dateTime = new Date(timestamp);

  // Convert to local time zone and format as a string
  const formattedDate = dateTime.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  });

  return formattedDate;
};
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) {
    return 'N/A';
  }
  if (typeof amount === 'string') {
    amount = parseFloat(amount);
  }
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}

export const formatDate = (timestamp) => {
  if (!timestamp) {
    return 'N/A';
  }

  return moment(timestamp, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY hh:mm a');

};

export const formatTime = (time) => {
  const now = moment();
  const notificationDate = moment(time);

  const isToday = now.isSame(notificationDate, 'day');
  const isYesterday = now.clone().subtract(1, 'day').isSame(notificationDate, 'day');

  let formattedDate;
  if (isToday) {
    formattedDate = `Today at ${notificationDate.format('h:mm A')}`;
  } else if (isYesterday) {
    formattedDate = `Yesterday at ${notificationDate.format('h:mm A')}`;
  } else {
    formattedDate = notificationDate.format('MMM D, YYYY h:mm A');
  }

  return formattedDate;
};
export const formatExactTimestamp = (time) => {
  if (!time) return "";
  const m = moment(time);
  if (!m.isValid()) return "";
  return m.format("MMM D, YYYY · h:mm A"); // Jan 12, 2026 · 6:58 AM
};

// List (relative)
export const formatRelativeNotificationTime = (time) => {
  if (!time) return "";
  const now = moment();
  const m = moment(time);
  if (!m.isValid()) return "";

  const diffSeconds = now.diff(m, "seconds");
  if (diffSeconds < 10) return "Just now";

  const diffMinutes = now.diff(m, "minutes");
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;

  const diffHours = now.diff(m, "hours");
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;

  const diffDays = now.startOf("day").diff(m.startOf("day"), "days");
  if (diffDays === 1) return "Yesterday";

  // 2 days ago / a month ago etc
  return m.fromNow();
};

export const copyToClipboard = (inputValue) => {
  const textarea = document.createElement('textarea')
  textarea.value = inputValue
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export const copyUrlToClipboard = async (url) => {
  // Copy URL as both plain text and HTML hyperlink so it appears clickable in email clients
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      const htmlContent = `<a href="${url}">${url}</a>`
      const textBlob = new Blob([url], { type: 'text/plain' })
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
      const clipboardItem = new ClipboardItem({
        'text/plain': textBlob,
        'text/html': htmlBlob
      })
      await navigator.clipboard.write([clipboardItem])
    } else {
      // Fallback for browsers that don't support ClipboardItem
      copyToClipboard(url)
    }
  } catch (e) {
    // Fallback to plain text copy
    copyToClipboard(url)
  }
}

export const initializeValues = (fields) => {
  return fields.reduce((acc, { name }) => {
    acc[name] = '';
    return acc;
  }, {});
};

export const capitalize = (str) => {
  return str.toLowerCase().split(' ').map(word => word[0].toUpperCase() + word.slice(1)).join(' ');
}

export const setSessionFilter = (name, value) => {
  try {
    const obj = JSON.parse(sessionStorage.getItem('filters') || '{}')
    obj[name] = value
    sessionStorage.setItem('filters', JSON.stringify(obj))
  } catch (e) {
  }
}

export const getSessionFilter = (name, defaultValue = null) => {
  try {
    const obj = JSON.parse(sessionStorage.getItem('filters') || '{}')
    return obj && obj[name] !== undefined ? obj[name] : defaultValue
  } catch (e) {
    return defaultValue
  }
}

export const calculateTotals = ({ parts = [], taxPercentage = 0, isTaxEnabled = true } = {}) => {

  const partsTotal = (parts || []).reduce((sum, p) => {
    const qty = Number(p?.quantity) || 0;
    const price = Number(p?.value) || 0;

    return sum + (qty * price);
  }, 0);

  const pctNum = Number(taxPercentage);
  const safePct = Number.isFinite(pctNum) ? pctNum : 0;

  const totalTax = isTaxEnabled ? partsTotal * (safePct / 100) : 0;
  const total = partsTotal + totalTax;

  return { partsTotal, totalTax, total };
};

export const resolveCssVarValue = (
  cssVar,
  { unit = 'number', fallback = 0 } = {}
) => {
  const tempElement = document.createElement('div');
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.width = `var(${cssVar})`;
  document.body.appendChild(tempElement);
  const computedValue = parseFloat(getComputedStyle(tempElement).width);
  document.body.removeChild(tempElement);
  if (Number.isNaN(computedValue)) {
    return unit === 'px' ? `${fallback}px` : fallback;
  }

  return unit === 'px' ? `${computedValue}px` : computedValue;
};